import { createSelector } from "@reduxjs/toolkit";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { RootState } from "@/redux/store";

export const selectPickup = (state: RootState) => state.clientBooking.pickup;
export const selectDestination = (state: RootState) => state.clientBooking.destination;
export const selectStops = (state: RootState) => state.clientBooking.stops;
export const selectActiveBookingField = (state: RootState) => state.clientBooking.activeField;
export const selectIsPinning = (state: RootState) => state.clientBooking.isPinning;
export const selectPickupFlightNumber = (state: RootState) => state.clientBooking.pickupFlightNumber;
/** "Delay driver arrival by", in minutes. `null` when never asked (DEV-1429). */
export const selectPickupFlightDelayMinutes = (state: RootState) => state.clientBooking.pickupFlightDelayMinutes ?? null;
export const selectPickupFlightLandingUsed = (state: RootState) => state.clientBooking.pickupFlightLandingUsed ?? null;
/** True while the committed pickup was derived from a flight that is no longer the selected one. */
export const selectPickupFlightChangedSinceCommit = (state: RootState) => state.clientBooking.pickupFlightChangedSinceCommit ?? false;
export const selectPickupFlightDerivedArrival = (state: RootState) => state.clientBooking.pickupFlightDerivedArrival ?? null;
export const selectPickupFlightDerivedArrivalSource = (state: RootState) => state.clientBooking.pickupFlightDerivedArrivalSource ?? null;
export const selectPickupFlightFrozenAt = (state: RootState) => state.clientBooking.pickupFlightFrozenAt ?? null;
export const selectPickupFlightFrozenReason = (state: RootState) => state.clientBooking.pickupFlightFrozenReason ?? null;
/**
 * Read-only replay of a closed / non-editable booking. The same fact `BookingViewOnlyContext` carries to
 * the panels, read off the draft so a hook can gate on it without being handed the context — the sheet
 * root's own hooks run OUTSIDE its `BookingViewOnlyProvider`.
 */
export const selectViewOnly = (state: RootState) => state.clientBooking.viewOnly ?? false;
export const selectBookingPassenger = (state: RootState) => state.clientBooking.passenger;
export const selectSessionGuests = (state: RootState) => state.clientBooking.sessionGuests;
export const selectPickupDatetime = (state: RootState) => state.clientBooking.pickupDatetime;
export const selectPaymentMethod = (state: RootState) => state.clientBooking.paymentMethod;
export const selectGuestPaymentMethodId = (state: RootState) => state.clientBooking.guestPaymentMethodId ?? null;
/** Guest flow only: the captured card's issuing country — feeds `payment_method_country` on the guest quote. */
export const selectGuestPaymentMethodCountry = (state: RootState) => state.clientBooking.guestPaymentMethodCountry ?? null;
/** Guest flow only: the guest's own contact details — the account-mint source on submit. */
export const selectGuestContact = (state: RootState) => state.clientBooking.guestContact ?? null;
export const selectBookingScope = (state: RootState) => state.clientBooking.scopeAccountId ?? null;
export const selectBookingVehicle = (state: RootState) => state.clientBooking.vehicle;
export const selectBookingTransferType = (state: RootState) => state.clientBooking.transferType;
export const selectNumOfPassengers = (state: RootState) => state.clientBooking.numOfPassengers;
export const selectNumOfBags = (state: RootState) => state.clientBooking.numOfBags;
export const selectBookingExtras = (state: RootState) => state.clientBooking.extras;
export const selectBookedBy = (state: RootState) => state.clientBooking.bookedBy;
export const selectReference = (state: RootState) => state.clientBooking.reference;
export const selectAdditionalInfo = (state: RootState) => state.clientBooking.additionalInfo;
export const selectPassengerDetails = (state: RootState) => state.clientBooking.passengers;
export const selectJobRate = (state: RootState) => state.clientBooking.jobRate;
export const selectBookedForDuration = (state: RootState) => state.clientBooking.bookedForDuration;
/** Operator-entered price for a fixed-rate transfer type (undefined for auto-quoted rates). */
export const selectFixedPrice = (state: RootState) => state.clientBooking.fixedPrice;
/** Edit sheet: the job id being edited (drives the "Update Booking" CTA), or null in create flows. */
export const selectEditJobId = (state: RootState) => state.clientBooking.editJobId ?? null;
/** Edit sheet: the as-hydrated draft snapshot to diff the live draft against. */
export const selectEditOriginal = (state: RootState) => state.clientBooking.editOriginal ?? null;

/** Whole draft — feeds buildBookingPayload/getMissingBookingFields (useBookingQuote). */
export const selectClientBookingDraft = (state: RootState) => state.clientBooking;

/** The POI currently in `activeField` (seeds the pin picker / shows which row is being edited). */
export const selectActiveFieldLocation = (state: RootState) => {
  const f = state.clientBooking.activeField;
  if (!f) return null;
  if (f.kind === "pickup") return state.clientBooking.pickup;
  if (f.kind === "destination") return state.clientBooking.destination;
  return state.clientBooking.stops[f.index] ?? null;
};

/**
 * Ordered, filled locations `[pickup, ...stops, destination]` — drives map markers and
 * the route polyline, and is the booking summary's view of the trip. Unfilled stops dropped.
 */
export const selectOrderedLocations = createSelector(
  [selectPickup, selectStops, selectDestination],
  (pickup, stops, destination): PointsOfInterest[] => [pickup, ...(stops ?? []), destination].filter((p): p is PointsOfInterest => !!p)
);
